import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  PaperAirplaneIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SignalIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import {
  FaqAccordion,
  PageCta,
  PageHero,
  SectionHeading,
} from '../../components/page-templates/SitePageTemplates';

const heroPanelItems = [
  {
    icon: PaperAirplaneIcon,
    title: 'Global air and sea freight',
    description: 'Multi-modal freight for ICT hardware coordinated through trusted logistics partners worldwide.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Secure handling for high-value tech',
    description: 'Protocols designed for servers, networking, and telecom equipment across every stage of transit.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Integrated with compliance',
    description: 'Freight execution stays aligned with customs clearance, duties, and regulatory requirements.',
  },
];

const challenges = [
  {
    icon: ShieldCheckIcon,
    title: 'High-value and sensitive equipment',
    description: 'Servers, networking, and telecom hardware need protected handling from origin to destination.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Complex routing across multiple countries',
    description: 'Multi-country programmes require coordination across carriers, hubs, and regulatory checkpoints.',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'Time-critical delivery windows',
    description: 'Launch dates, rack dates, and go-live schedules leave little room for transit delays.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Coordination with customs and compliance',
    description: 'Freight execution must stay aligned with trade documentation and regulatory requirements.',
  },
];

const serviceCards = [
  {
    icon: PaperAirplaneIcon,
    title: 'Air Freight',
    description: 'Fast and reliable global air freight for time-sensitive technology shipments.',
    features: [
      'Priority air freight for ICT hardware',
      'Secure handling for high-value equipment',
      'Global airline network coverage',
      'Door-to-door transport coordination',
      'Integration with customs clearance services',
    ],
  },
  {
    icon: TruckIcon,
    title: 'Sea Freight',
    description: 'Cost-efficient transport for large technology shipments and infrastructure equipment.',
    features: [
      'Full container load (FCL) and shared container options',
      'Secure transport for racks, servers, and infrastructure hardware',
      'Global ocean freight network',
      'Consolidation and routing optimisation',
      'Coordination with customs clearance',
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Cargo Insurance',
    description: 'Additional protection for high-value technology equipment during transit.',
    features: [
      'Coverage for international hardware shipments',
      'Protection against transport damage or loss',
      'Insurance solutions tailored for ICT equipment',
      'Risk management for global freight movements',
    ],
  },
];

const advantages = [
  'Experience transporting servers, networking equipment, and telecom hardware',
  'Global freight network supporting international shipments',
  'Integration with trade compliance and customs services',
  'Secure handling for high-value technology equipment',
];

const supportedHardware = [
  { icon: BuildingOffice2Icon, label: 'Data centre infrastructure' },
  { icon: ServerStackIcon, label: 'Servers and storage equipment' },
  { icon: SignalIcon, label: 'Networking hardware' },
  { icon: TruckIcon, label: 'Telecom infrastructure' },
  { icon: CpuChipIcon, label: 'End-point devices and peripherals' },
  { icon: GlobeAltIcon, label: 'Branch office and remote-site equipment' },
];

const faqs = [
  {
    question: 'What freight services does TrueLog provide?',
    answer:
      'TrueLog provides global air and sea freight services for ICT hardware and technology infrastructure. Our services include international transport coordination, cargo insurance, and integration with customs and trade compliance.',
  },
  {
    question: 'Can TrueLog transport high-value technology equipment?',
    answer:
      'Yes. TrueLog specialises in the transport of servers, networking equipment, telecom hardware, and other high-value technology assets requiring secure handling and specialised logistics coordination.',
  },
  {
    question: 'Does TrueLog provide cargo insurance?',
    answer:
      'Yes. Cargo insurance can be arranged to protect ICT hardware shipments against potential damage or loss during international transport.',
  },
  {
    question: 'Does TrueLog manage customs clearance for freight shipments?',
    answer:
      'Yes. TrueLog integrates global freight services with customs clearance and trade compliance, ensuring shipments move efficiently across international borders. TrueLog organises in-house customs clearance in Singapore.',
  },
];

const ctaPills = [
  'Global air and sea freight for ICT hardware',
  'Secure transport for high-value technology',
  'Integrated with compliance and customs services',
];

const FreightForwarding: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Global Transport & Freight"
        title="Speed Without Guesswork: Global Transport for ICT Infrastructure"
        highlight="Global Transport for ICT Infrastructure"
        description="Move servers, networking equipment, and technology infrastructure across borders with reliable global freight. TrueLog manages the international movement of ICT hardware through a network of trusted logistics partners, ensuring safe handling, efficient routing, and full coordination across the transport chain."
        heroIcon={TruckIcon}
        accentIcon={GlobeAltIcon}
        actions={[{ href: '/contact-us', label: 'Talk to a Specialist' }]}
        badges={[
          'Global air and sea freight for ICT hardware',
          'Secure transport for high-value technology equipment',
          'Integrated with TrueLog trade compliance and customs services',
        ]}
        metrics={[
          { value: '150+', label: 'Countries reached' },
          { value: '24/7', label: 'Shipment coordination' },
          { value: 'Air + Sea', label: 'Multi-modal freight' },
          { value: '1', label: 'Accountable partner' },
        ]}
        panelEyebrow="Freight control"
        panelTitle="Built for ICT infrastructure movement"
        panelDescription="Transport planned around hardware sensitivity, time-critical windows, and compliance-aware routing."
        panelItems={heroPanelItems}
      />

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Why It Matters"
            title="Why ICT Hardware Requires Specialist Freight"
            description="Technology infrastructure is high-value, sensitive, and often time-critical. Standard freight handling may expose equipment to unnecessary risk or delays."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-base leading-8 text-slate-600 sm:text-lg"
          >
            Servers, networking equipment, and telecom hardware require careful packaging, secure handling, and coordinated logistics to ensure equipment arrives safely and on schedule.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            Challenges in transporting ICT infrastructure often include:
          </motion.p>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {challenges.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-12 flex max-w-3xl flex-col items-center gap-5 text-center"
          >
            <p className="text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog specialises in managing the transport of <span className="font-semibold text-slate-950">technology infrastructure shipments</span>, ensuring safe movement from origin to destination.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Talk to a Specialist
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Our Services"
            title="Our Global Transport & Freight Services"
            description="Three freight services that keep ICT hardware moving reliably across borders with full transport, insurance, and compliance coordination."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {serviceCards.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="flex flex-col rounded-[30px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex w-fit rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                  <service.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-slate-950">{service.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>
                <ul className="mt-5 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                      <CheckCircleIcon className="mt-1 h-4 w-4 flex-shrink-0 text-[#0E9ED5]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact-us"
                  className="mt-auto inline-flex w-fit items-center gap-2 pt-6 text-sm font-bold uppercase tracking-[0.14em] text-[#2447d8] hover:text-[#0E9ED5]"
                >
                  Get a Quote
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
          >
            <span className="eyebrow-pill">Why TrueLog</span>
            <h2 className="mt-5 text-[30px] font-bold tracking-tight text-slate-950 sm:text-[38px]">
              Why ICT Companies Choose TrueLog
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog specialises in the global movement of ICT hardware and infrastructure.
            </p>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]">Key advantages:</p>
          </motion.div>

          <div className="grid gap-4">
            {advantages.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                className="flex items-start gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5 text-white">
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <p className="text-sm leading-7 text-slate-700">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Integrated Model"
            title="Built for Global ICT Logistics"
            description="Transport is only one part of the global ICT logistics chain."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-base leading-8 text-slate-600 sm:text-lg"
          >
            TrueLog integrates <span className="font-semibold text-slate-950">freight transport, trade compliance, customs clearance, and final delivery</span>, ensuring that hardware moves efficiently from origin to destination.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            We support the global movement of:
          </motion.p>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {supportedHardware.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-center gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="rounded-2xl bg-sky-50 p-3 text-sky-600">
                  <item.icon className="h-6 w-6" />
                </div>
                <p className="text-base font-semibold text-slate-950">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="FAQ"
            title="Common questions about global freight"
            description="The operational questions that come up most often when planning international movement of ICT hardware."
          />
          <div className="mx-auto max-w-4xl">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>

      <PageCta
        title="Move Your ICT Hardware Globally"
        description="Whether you are shipping a single server or coordinating hardware movement across multiple countries, TrueLog ensures reliable international transport for your technology infrastructure."
        pills={ctaPills}
        primaryHref="/contact-us"
        primaryLabel="Contact Us"
        secondaryHref="/services"
        secondaryLabel="Explore services"
      />
    </div>
  );
};

export default FreightForwarding;
